import { useLocation, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from 'react';
import { navigationItems, NavItem } from "../components/NavigationItems";
import { systemCustomerAssociationsQueryOptions } from "@api/customers/customerQueries";

const isPathPrefixMatch = (pathname: string, path: string) =>
  path === '/'
    ? pathname === '/'
    : pathname === path || pathname.startsWith(path + '/');

const collectAllPaths = (items: NavItem[]): string[] =>
  items.flatMap(item => [
    ...(item.path ? [item.path] : []),
    ...(item.children ? collectAllPaths(item.children) : []),
  ]);

const allNavPaths = collectAllPaths(navigationItems);

// Most-specific-wins: a path is active only if no other nav path is a
// longer match for the current pathname. Prevents parent entries like
// "/admin" from highlighting on "/admin/license-viewer".
const isMostSpecificMatch = (pathname: string, path: string) => {
  if (!isPathPrefixMatch(pathname, path)) return false;
  return !allNavPaths.some(other =>
    other !== path && other.length > path.length && isPathPrefixMatch(pathname, other),
  );
};

export const useNavigation = () => {
  const {pathname} = useLocation();
  const params = useParams({strict: false});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // On /systems/:id the URL matches no nav item, so resolve the item by context:
  // Customers if the system has any associations, Unassigned Systems otherwise.
  const systemId = 'systemId' in params ? (params as { systemId: string }).systemId : undefined;
  const {data: systemCustomerData, isLoading} = useQuery({
    ...systemCustomerAssociationsQueryOptions(systemId ?? ''),
    enabled: !!systemId,
  });
  const contextualActivePath = systemId && !isLoading
    ? ((systemCustomerData?.systemCustomerAssociationDtos.length ?? 0) > 0 ? '/customers' : '/systems/unassigned')
    : null;

  const isActive = (path: string) =>
    contextualActivePath ? path === contextualActivePath : isMostSpecificMatch(pathname, path);

  const hasActiveChildren = (item: NavItem): boolean =>
    item.children?.some(child => child.path && isActive(child.path)) ?? false;

  const toggleSection = (label: string) => {
    setExpandedSections(prev => ({...prev, [label]: !prev[label]}));
  };

  const getItemActiveState = (item: NavItem) =>
    item.path ? isActive(item.path) : hasActiveChildren(item);

  return {expandedSections, toggleSection, getItemActiveState};
};
